import { useState, useEffect } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import AdminChallenges from "./AdminChallenges";

const AVAILABLE_PROGRAMS = [
  { value: "all", label: "All Users" },
  { value: "quit-the-quit-beta", label: "Quit the Quit Beta" },
  { value: "quit-the-quit", label: "Quit the Quit" },
  { value: "momentum", label: "Momentum" },
];

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f8fbff",
  color: "#1e3a5f",
  fontSize: "14px",
  boxSizing: "border-box",
};

const btnPrimary = {
  padding: "10px 18px",
  borderRadius: "12px",
  border: "none",
  background: "#38bdf8",
  color: "#fff",
  fontWeight: "bold",
  cursor: "pointer",
  fontSize: "14px",
};

const btnGhost = {
  padding: "8px 14px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f5faff",
  color: "#4b6b8a",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: "bold",
};

const SECTIONS = [
  { key: "sideQuests", label: "Side Quests", emoji: "✨" },
  { key: "challenges", label: "Challenges", emoji: "🏆" },
  { key: "bonusXPOpportunities", label: "Bonus XP", emoji: "💎" },
  { key: "badges", label: "Badges", emoji: "🏅" },
];

const WEEKLY_CHALLENGES_SECTION = { key: "weeklyChallenges", label: "Weekly Challenges", emoji: "⭐" };

function emptyItem(section) {
  const base = {
    id: `${section}-${Date.now()}`,
    emoji: "✨",
    title: "",
    description: "",
    xp: 10,
    active: true,
    targetProgram: "all",
  };
  if (section === "weeklyChallenges") {
    return { ...base, text: "", startDate: "", endDate: "" };
  }
  if (section === "badges") {
    return { ...base, name: "", subtitle: "", type: "xp", threshold: 100 };
  }
  return base;
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      style={{
        width: "52px",
        height: "28px",
        borderRadius: "999px",
        border: "none",
        background: value ? "#2563eb" : "#d1c4e9",
        cursor: "pointer",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.2s",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "3px",
          left: value ? "27px" : "3px",
          width: "22px",
          height: "22px",
          borderRadius: "999px",
          background: "#fff",
          transition: "left 0.2s",
        }}
      />
    </button>
  );
}

function ItemEditor({ item, section, onSave, onCancel }) {
  const [draft, setDraft] = useState({ ...item });

  function set(key, val) {
    setDraft((prev) => ({ ...prev, [key]: val }));
  }

  const isChallenge = section === "weeklyChallenges";
  const isBadge = section === "badges";

  return (
    <div style={{ display: "grid", gap: "14px" }}>
      {/* Emoji + Title row */}
      <div style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: "10px" }}>
        <div>
          <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Emoji</div>
          <input
            type="text"
            value={draft.emoji}
            onChange={(e) => set("emoji", e.target.value)}
            style={{ ...inputStyle, textAlign: "center", fontSize: "20px" }}
          />
        </div>
        <div>
          <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>
            {isChallenge ? "Challenge Text" : isBadge ? "Badge Name" : "Title"}
          </div>
          <input
            type="text"
            value={isChallenge ? draft.text || "" : isBadge ? draft.name || "" : draft.title || ""}
            onChange={(e) => {
              if (isChallenge) set("text", e.target.value);
              else if (isBadge) set("name", e.target.value);
              else set("title", e.target.value);
            }}
            placeholder={isChallenge ? "e.g. Drink 8 glasses of water every day" : isBadge ? "e.g. Streak Starter" : "e.g. Take a 10-min walk"}
            style={inputStyle}
          />
        </div>
      </div>

      {/* Description / Subtitle */}
      {!isBadge && (
        <div>
          <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Description (optional)</div>
          <textarea
            value={draft.description || ""}
            onChange={(e) => set("description", e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>
      )}
      {isBadge && (
        <div>
          <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Subtitle</div>
          <input
            type="text"
            value={draft.subtitle || ""}
            onChange={(e) => set("subtitle", e.target.value)}
            placeholder="e.g. Complete a 7-day streak"
            style={inputStyle}
          />
        </div>
      )}

      {/* XP + Active row (non-challenge, non-badge) */}
      {!isChallenge && !isBadge && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "10px", alignItems: "end" }}>
            <div>
              <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>XP Reward</div>
              <input
                type="number"
                min="0"
                value={draft.xp}
                onChange={(e) => set("xp", Number(e.target.value))}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Active</div>
              <Toggle value={draft.active} onChange={(v) => set("active", v)} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Start Date (optional)</div>
              <input type="date" value={draft.startDate || ""} onChange={(e) => set("startDate", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>End Date (optional)</div>
              <input type="date" value={draft.endDate || ""} onChange={(e) => set("endDate", e.target.value)} style={inputStyle} />
            </div>
          </div>
        </>
      )}

      {/* Badge-specific: type + threshold + active */}
      {isBadge && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "10px", alignItems: "end" }}>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Type</div>
            <select value={draft.type || "xp"} onChange={(e) => set("type", e.target.value)} style={inputStyle}>
              <option value="xp">Total XP</option>
              <option value="level">Level</option>
              <option value="fullQuestStreak">Full Quest Streak</option>
              <option value="nutritionStreak">Nutrition Streak</option>
              <option value="movementStreak">Movement Streak</option>
              <option value="mindsetStreak">Mindset Streak</option>
              <option value="weeklyChallengeStreak">Weekly Challenge Streak</option>
              <option value="loginStreak">Login Streak</option>
              <option value="loginAfterMissedDay">Login After Missed Day</option>
              <option value="reviveUsed">Revive Used (count)</option>
              <option value="habitsAfterRevive">Habits Completed After Revive</option>
              <option value="shieldEarned">Shield Earned (count)</option>
              <option value="sideQuestCount">Side Quests Completed (count)</option>
            </select>
          </div>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Threshold</div>
            <input
              type="number"
              min="1"
              value={draft.threshold || 1}
              onChange={(e) => set("threshold", Number(e.target.value))}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Active</div>
            <Toggle value={draft.active} onChange={(v) => set("active", v)} />
          </div>
        </div>
      )}

      {/* Weekly challenge: XP + dates + active */}
      {isChallenge && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "10px", alignItems: "end" }}>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>XP Reward</div>
            <input
              type="number"
              min="0"
              value={draft.xp}
              onChange={(e) => set("xp", Number(e.target.value))}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Start Date</div>
            <input
              type="date"
              value={draft.startDate || ""}
              onChange={(e) => set("startDate", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>End Date</div>
            <input
              type="date"
              value={draft.endDate || ""}
              onChange={(e) => set("endDate", e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "4px" }}>Active</div>
            <Toggle value={draft.active} onChange={(v) => set("active", v)} />
          </div>
        </div>
      )}

      {/* Target Program */}
      <div>
        <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "6px" }}>Show To</div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {AVAILABLE_PROGRAMS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => set("targetProgram", p.value)}
              style={{
                padding: "6px 12px",
                borderRadius: "10px",
                border: (draft.targetProgram || "all") === p.value ? "2px solid #2563eb" : "2px solid #bfdbfe",
                background: (draft.targetProgram || "all") === p.value ? "#e0f2fe" : "#f5faff",
                color: "#1e3a5f",
                fontWeight: "bold",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Save / Cancel */}
      <div style={{ display: "flex", gap: "8px" }}>
        <button type="button" onClick={() => onSave(draft)} style={btnPrimary}>Save</button>
        <button type="button" onClick={onCancel} style={btnGhost}>Cancel</button>
      </div>
    </div>
  );
}

export default function AdminGameContent({ sectionOverride = null }) {
  const [gameContent, setGameContent] = useState({
    sideQuests: [],
    weeklyChallenges: [],
    bonusXPOpportunities: [],
    badges: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(sectionOverride || "sideQuests");
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "appConfig", "gameContent"));
      if (snap.exists()) {
        const d = snap.data();
        setGameContent({
          sideQuests: d.sideQuests || [],
          weeklyChallenges: d.weeklyChallenges || [],
          bonusXPOpportunities: d.bonusXPOpportunities || [],
          badges: d.badges || [],
          leaderboardVisible: d.leaderboardVisible || false,
        });
      }
    } catch (err) {
      console.error("Error loading game content:", err);
    } finally {
      setLoading(false);
    }
  }

  async function save(updated) {
    setSaving(true);
    try {
      await setDoc(doc(db, "appConfig", "gameContent"), updated, { merge: true });
      setGameContent(updated);
    } catch (err) {
      console.error("Error saving game content:", err);
    } finally {
      setSaving(false);
    }
  }

  function getItems() {
    if (activeSection === "bonusXPOpportunities") return gameContent.bonusXPOpportunities || [];
    return gameContent[activeSection] || [];
  }

  function setItems(items) {
    const updated = { ...gameContent, [activeSection]: items };
    save(updated);
  }

  function handleSaveItem(draft) {
    const items = getItems();
    const exists = items.find((i) => i.id === draft.id);
    const updated = exists ? items.map((i) => (i.id === draft.id ? draft : i)) : [...items, draft];
    setItems(updated);
    setEditingItem(null);
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this item?")) return;
    setItems(getItems().filter((i) => i.id !== id));
  }

  function handleToggleActive(id) {
    setItems(getItems().map((i) => (i.id === id ? { ...i, active: !i.active } : i)));
  }

  function handleMoveUp(idx) {
    const items = [...getItems()];
    if (idx === 0) return;
    [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
    setItems(items);
  }

  function handleMoveDown(idx) {
    const items = [...getItems()];
    if (idx === items.length - 1) return;
    [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
    setItems(items);
  }

  if (loading) {
    return <div style={{ color: "#4b6b8a", padding: "16px" }}>Loading game content...</div>;
  }

  const items = getItems();
  const today = new Date().toISOString().slice(0, 10);
  const visibleSections = sectionOverride === "weeklyChallenges"
    ? [WEEKLY_CHALLENGES_SECTION]
    : SECTIONS;
  const currentSection = visibleSections.find((s) => s.key === activeSection) || visibleSections[0];

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Section tabs — hidden when a section is locked via sectionOverride */}
      {!sectionOverride && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => { setActiveSection(s.key); setEditingItem(null); }}
              style={{
                padding: "8px 14px",
                borderRadius: "12px",
                border: activeSection === s.key ? "2px solid #2563eb" : "2px solid #bfdbfe",
                background: activeSection === s.key ? "#e0f2fe" : "#f8fbff",
                color: "#1e3a5f",
                fontWeight: "bold",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Challenges section delegates entirely to AdminChallenges */}
      {activeSection === "challenges" && <AdminChallenges />}

      {activeSection !== "challenges" && <>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>
            {currentSection?.emoji} {currentSection?.label}
          </div>
          <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "2px" }}>
            {activeSection === "weeklyChallenges" && "Set start/end dates to schedule challenges. The active challenge for the current week is shown to users. XP defaults to 10."}
            {activeSection === "sideQuests" && "Active side quests appear in the Quests tab. Users can complete one per day for XP."}
            {activeSection === "bonusXPOpportunities" && "Bonus XP items are shown in the Quests tab as one-time opportunities."}
            {activeSection === "badges" && "Badges are awarded automatically when users hit the threshold."}
          </div>
        </div>
        {!editingItem && (
          <button
            type="button"
            onClick={() => setEditingItem({ item: emptyItem(activeSection), isNew: true })}
            style={{ ...btnPrimary, flexShrink: 0 }}
          >
            + Add
          </button>
        )}
      </div>

      {/* Editor */}
      {editingItem && (
        <div style={{ background: "#f0f9ff", borderRadius: "18px", padding: "18px", border: "2px solid #38bdf8" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", marginBottom: "14px" }}>
            {editingItem.isNew ? "New Item" : "Edit Item"}
          </div>
          <ItemEditor
            item={editingItem.item}
            section={activeSection}
            onSave={handleSaveItem}
            onCancel={() => setEditingItem(null)}
          />
        </div>
      )}

      {/* Items list */}
      {items.length === 0 ? (
        <div style={{ background: "#f8fbff", borderRadius: "18px", padding: "24px", border: "2px solid #bfdbfe", color: "#4b6b8a", textAlign: "center" }}>
          No items yet. Tap + Add above.
        </div>
      ) : (
        <div style={{ display: "grid", gap: "10px" }}>
          {[...items]
            .map((item, originalIdx) => ({ item, originalIdx }))
            .sort((a, b) => {
              // Most recent start date first; undated items go to the bottom
              const da = a.item.startDate || "";
              const db_ = b.item.startDate || "";
              if (!da && !db_) return 0;
              if (!da) return 1;
              if (!db_) return -1;
              return db_.localeCompare(da);
            })
            .map(({ item, originalIdx }) => {
            const isChallenge = activeSection === "weeklyChallenges";
            const isSideQuest = activeSection === "sideQuests" || activeSection === "bonusXPOpportunities";
            const isBadge = activeSection === "badges";
            const displayName = isChallenge ? item.text : isBadge ? item.name : item.title;
            const targetLabel = AVAILABLE_PROGRAMS.find((p) => p.value === (item.targetProgram || "all"))?.label || "All Users";

            const isExpired = item.endDate && today > item.endDate;
            const isUpcoming = item.startDate && today < item.startDate;

            let dateStatus = null;
            if ((isChallenge || isSideQuest) && item.startDate && item.endDate) {
              if (isUpcoming) {
                dateStatus = { label: `Starts ${item.startDate}`, color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" };
              } else if (isExpired) {
                dateStatus = { label: `Ended ${item.endDate}`, color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1" };
              } else {
                dateStatus = { label: isChallenge ? "● This week" : "● Live now", color: "#16a34a", bg: "#dcfce7", border: "#86efac" };
              }
            }

            // Badge shows "Ended" if the end date passed, even if admin left the toggle on
            const statusLabel = isExpired ? "Ended" : item.active ? "Active" : "Inactive";
            const statusStyle = isExpired
              ? { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" }
              : item.active
                ? { bg: "#dcfce7", color: "#16a34a", border: "#86efac" }
                : { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" };

            return (
              <div
                key={item.id}
                style={{
                  background: isExpired ? "#f8f8f8" : item.active ? "#f8fbff" : "#f1f5f9",
                  borderRadius: "16px",
                  padding: "14px 16px",
                  border: isExpired ? "2px solid #e2e8f0" : item.active ? "2px solid #bfdbfe" : "2px solid #e2e8f0",
                  opacity: isExpired ? 0.7 : item.active ? 1 : 0.75,
                }}
              >
                {/* Top row: emoji + info */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <span style={{ fontSize: "22px", flexShrink: 0 }}>{item.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "4px", wordBreak: "break-word" }}>
                      {displayName || "(no name)"}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                      {!isBadge && (
                        <span style={{ fontSize: "11px", color: "#6b8cb0", fontWeight: "bold" }}>+{item.xp || 10} XP</span>
                      )}
                      {isBadge && (
                        <span style={{ fontSize: "11px", color: "#6b8cb0" }}>{item.type} ≥ {item.threshold}</span>
                      )}
                      {dateStatus && (
                        <span style={{ padding: "2px 7px", borderRadius: "999px", fontSize: "10px", fontWeight: "bold", background: dateStatus.bg, color: dateStatus.color, border: `1px solid ${dateStatus.border}` }}>
                          {dateStatus.label}
                        </span>
                      )}
                      <span style={{
                        padding: "2px 7px", borderRadius: "999px", fontSize: "10px", fontWeight: "bold",
                        background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`,
                      }}>
                        {statusLabel}
                      </span>
                      <span style={{ padding: "2px 7px", borderRadius: "999px", fontSize: "10px", fontWeight: "bold", background: "#f0f9ff", color: "#2563eb", border: "1px solid #bfdbfe" }}>
                        {targetLabel}
                      </span>
                    </div>
                    {item.description && <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "4px" }}>{item.description}</div>}
                    {item.subtitle && <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "4px" }}>{item.subtitle}</div>}
                    {(isChallenge || isSideQuest) && item.startDate && (
                      <div style={{ color: "#9ca3af", fontSize: "11px", marginTop: "2px" }}>
                        {item.startDate} → {item.endDate || "no end"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons row */}
                <div style={{ display: "flex", gap: "6px", marginTop: "10px", flexWrap: "wrap" }}>
                  <button type="button" onClick={() => handleMoveUp(originalIdx)} style={{ ...btnGhost, padding: "4px 10px", fontSize: "12px" }}>↑</button>
                  <button type="button" onClick={() => handleMoveDown(originalIdx)} style={{ ...btnGhost, padding: "4px 10px", fontSize: "12px" }}>↓</button>
                  <Toggle value={item.active} onChange={() => handleToggleActive(item.id)} />
                  <button type="button" onClick={() => setEditingItem({ item: { ...item }, isNew: false })} style={{ ...btnGhost, padding: "4px 12px" }}>✏️ Edit</button>
                  <button type="button" onClick={() => handleDelete(item.id)} style={{ ...btnGhost, padding: "4px 12px", color: "#dc2626", borderColor: "#fca5a5" }}>✕ Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {saving && (
        <div style={{ color: "#4b6b8a", fontSize: "12px", textAlign: "right" }}>Saving...</div>
      )}
      </>}
    </div>
  );
}
